/**
 * KPI Scorecard Page
 *
 * Main page showing the KPI scorecard table with period selector.
 * Double-click a KPI name to edit it in a right-side panel.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { RefreshCw, Plus, BarChart3, CheckCircle, Download, X, Users, FilterX } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { SlidePanel } from '@/shared/components';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCanManageInTeams } from '@/hooks/useSession';
import { useKpiScorecard, useKpiDefinition, useKpiCompletionStats, useCreateKpiValueOptimistic, useClearKpiPeriod, kpiKeys } from '../hooks/useKpis';
import { useKpiOrder } from '../hooks/useKpiOrder';
import { KpiScorecard, KpiScorecardSkeleton, ColumnVisibilityToggle } from '../components/KpiScorecard';
import type { KpiStatusFilter } from '../components/KpiScorecard';
import { KpiForm } from '../components/KpiForm';
import { KpiGuide } from '../components/KpiGuide';
import { useTeams } from '@features/rocks/hooks/useTeams';
import { useDefaultTeamId, persistTeamSelection } from '../hooks/useUserTeams';
import type { KpiPeriodType, KpiCategory, ScorecardFilters } from '../types';

const PERIOD_OPTIONS: { value: KpiPeriodType; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const PERIOD_TYPE_STORAGE_KEY = 'kpi-scorecard-period-type';
const PANEL_STORAGE_KEY = 'atek_kpi_panel_width';

function usePersistedState<T>(field: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const key = `atek-kpi-scorecard-${field}`;
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  }, [key, value]);
  return [value, setValue];
}

type KpiTab = 'scorecard' | 'guide';

const TABS: { value: KpiTab; label: string }[] = [
  { value: 'scorecard', label: 'Scorecard' },
  { value: 'guide', label: 'Guide' },
];

export function KpiScorecardPage() {
  const queryClient = useQueryClient();
  const { reorder } = useKpiOrder();
  // Page-level "can do anything" gate: any team member (or Leadership) can
  // create + manage their team's KPIs. Per-row edit access is checked
  // separately in the scorecard cell handlers using the KPI's team_ids.
  const canManage = useCanManageInTeams();

  // URL-driven tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: KpiTab = (searchParams.get('tab') as KpiTab) || 'scorecard';
  const setTab = (tab: KpiTab) => {
    setSearchParams((prev) => { prev.set('tab', tab); return prev; }, { replace: true });
  };
  const [periodType, setPeriodType] = useState<KpiPeriodType>(() => {
    const stored = localStorage.getItem(PERIOD_TYPE_STORAGE_KEY);
    if (stored && PERIOD_OPTIONS.some((opt) => opt.value === stored)) {
      return stored as KpiPeriodType;
    }
    return 'monthly';
  });

  const handlePeriodTypeChange = (value: KpiPeriodType) => {
    setPeriodType(value);
    localStorage.setItem(PERIOD_TYPE_STORAGE_KEY, value);
  };
  const { defaultTeamId, isLoading: teamsLoading } = useDefaultTeamId();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedOwnerIds, setSelectedOwnerIds] = usePersistedState<string[]>('owner-ids', []);
  const [teamInitialized, setTeamInitialized] = useState(false);
  const [searchText, setSearchText] = usePersistedState<string>('search', '');
  const [selectedCategory, setSelectedCategory] = usePersistedState<KpiCategory | '_all'>('category', '_all');
  const [selectedStatus, setSelectedStatus] = usePersistedState<KpiStatusFilter>('status', 'all');
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set());
  const [editingKpiId, setEditingKpiId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'create' | 'edit' | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [highlightedKpiId, setHighlightedKpiId] = useState<string | null>(null);

  const { data: teams = [] } = useTeams();

  // Build team ID → name map for scorecard display
  const teamMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of teams) map[t.id] = t.name;
    return map;
  }, [teams]);

  // Auto-default team filter
  useEffect(() => {
    if (!teamInitialized && !teamsLoading) {
      setSelectedTeamId(defaultTeamId);
      setTeamInitialized(true);
    }
  }, [teamsLoading, defaultTeamId, teamInitialized]);

  // Draggable panel
  const scorecardFilters: ScorecardFilters = useMemo(() => ({
    period_type: periodType,
    ...(selectedTeamId ? { team_id: selectedTeamId } : {}),
    ...(selectedOwnerIds.length > 0 ? { owner_ids: selectedOwnerIds } : {}),
  }), [periodType, selectedTeamId, selectedOwnerIds]);

  const { data: scorecard, isLoading, isFetching } = useKpiScorecard(scorecardFilters);
  const { data: editingKpiDefinition } = useKpiDefinition(editingKpiId ?? '');
  const { data: completionStats } = useKpiCompletionStats({ period_type: periodType, team_id: selectedTeamId });

  const createValueMutation = useCreateKpiValueOptimistic(scorecardFilters);
  const clearCellMutation = useClearKpiPeriod();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: kpiKeys.all });
  };

  const handleSaveValue = useCallback(
    async (kpiId: string, datetime: string, value: number, notes?: string) => {
      const period = scorecard?.periods.find((p) => {
        const periodStart = new Date(p.start);
        const periodEnd = new Date(p.end);
        const valueDate = new Date(datetime);
        return valueDate >= periodStart && valueDate <= periodEnd;
      });
      const cellKey = period ? `${kpiId}-${period.start}` : kpiId;

      setSavingCells((prev) => new Set(prev).add(cellKey));
      try {
        await createValueMutation.mutateAsync({
          kpi_id: kpiId,
          datetime,
          value,
          notes,
        });
      } finally {
        setSavingCells((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }
    },
    [createValueMutation, scorecard?.periods]
  );

  const handleClearValue = useCallback(
    async (kpiId: string, periodStart: string, periodEnd: string) => {
      const cellKey = `${kpiId}-${periodStart}`;
      setSavingCells((prev) => new Set(prev).add(cellKey));
      try {
        await clearCellMutation.mutateAsync({ kpiId, periodStart, periodEnd });
      } finally {
        setSavingCells((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }
    },
    [clearCellMutation]
  );

  // Backend returns categories already sorted by sort_order — no client-side reorder needed
  // Apply client-side filters on top of server-side (ensures correctness regardless of cache)
  const orderedCategories = useMemo(() => {
    const cats = scorecard?.categories ?? [];
    const q = searchText.trim().toLowerCase();
    const hasFilter =
      selectedOwnerIds.length > 0 ||
      q.length > 0 ||
      selectedCategory !== '_all' ||
      selectedStatus !== 'all';
    if (!hasFilter) return cats;
    return cats
      .filter((cat) => selectedCategory === '_all' || cat.name === selectedCategory)
      .map((cat) => ({
        ...cat,
        kpis: cat.kpis.filter((kpi) => {
          if (selectedOwnerIds.length > 0 && (!kpi.owner_id || !selectedOwnerIds.includes(kpi.owner_id))) return false;
          if (q.length > 0 && !kpi.name.toLowerCase().includes(q)) return false;
          if (selectedStatus === 'on_track' && kpi.on_track !== true) return false;
          if (selectedStatus === 'off_track' && kpi.on_track !== false) return false;
          if (selectedStatus === 'no_data' && kpi.on_track !== null) return false;
          return true;
        }),
      }))
      .filter((cat) => cat.kpis.length > 0);
  }, [scorecard?.categories, selectedOwnerIds, searchText, selectedCategory, selectedStatus]);

  const totalKpiCount = useMemo(() => {
    return orderedCategories.reduce((total, cat) => total + cat.kpis.length, 0);
  }, [orderedCategories]);

  const hasActiveFilters = useMemo(() => (
    searchText.trim().length > 0
    || selectedCategory !== '_all'
    || selectedStatus !== 'all'
    || selectedOwnerIds.length > 0
    || selectedTeamId !== null
  ), [searchText, selectedCategory, selectedStatus, selectedOwnerIds, selectedTeamId]);

  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setSelectedCategory('_all');
    setSelectedStatus('all');
    setSelectedOwnerIds([]);
    setSelectedTeamId(null);
    persistTeamSelection(null);
  }, []);

  const handleCreateKpi = useCallback(() => {
    setEditingKpiId(null);
    setPanelMode('create');
    setPanelOpen(true);
  }, []);

  const handleEditKpi = useCallback((kpiId: string) => {
    setEditingKpiId(kpiId);
    setPanelMode('edit');
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    const closedId = editingKpiId;
    setEditingKpiId(null);
    setPanelMode(null);
    setPanelOpen(false);
    // Brief highlight on the edited row
    if (closedId) {
      setHighlightedKpiId(closedId);
    }
  }, [editingKpiId]);

  // Clear highlight after animation
  useEffect(() => {
    if (!highlightedKpiId) return;
    const timer = setTimeout(() => setHighlightedKpiId(null), 2000);
    return () => clearTimeout(timer);
  }, [highlightedKpiId]);

  const handleExportCsv = useCallback(() => {
    if (!scorecard || totalKpiCount === 0) return;

    const periods = scorecard.periods;
    const allKpis = orderedCategories.flatMap((cat) =>
      cat.kpis.map((kpi) => ({ ...kpi, categoryName: cat.name }))
    );

    // Build header row
    const headers = ['Category', 'KPI', 'Owner', 'Goal', 'Avg', 'Total', ...periods.map((p) => p.label)];

    // Build data rows
    const rows = allKpis.map((kpi) => {
      const goal = kpi.goal_value != null
        ? `${kpi.goal_value}${kpi.goal_unit ? ` ${kpi.goal_unit}` : ''}`
        : kpi.goal_min != null && kpi.goal_max != null
          ? `${kpi.goal_min}-${kpi.goal_max}${kpi.goal_unit ? ` ${kpi.goal_unit}` : ''}`
          : '';

      const values = periods.map((period) => {
        const pv = kpi.values.find((v) => v.period_start === period.start);
        if (!pv || pv.value === null) return '';
        return kpi.show_average ? (pv.value_avg ?? pv.value) : kpi.show_total ? (pv.value_sum ?? pv.value) : pv.value;
      });

      return [
        kpi.categoryName,
        kpi.name,
        kpi.owner_name ?? '',
        goal,
        kpi.average != null ? kpi.average : '',
        kpi.total != null ? kpi.total : '',
        ...values,
      ];
    });

    // Escape CSV values
    const escapeCsv = (val: unknown) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kpi-scorecard-${periodType}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [scorecard, orderedCategories, totalKpiCount, periodType]);

  // Completion stats for toolbar
  const completionLabel = useMemo(() => {
    if (!completionStats?.overall) return null;
    const { completed, total, percentage } = completionStats.overall;
    return { completed, total, percentage };
  }, [completionStats]);

  return (
    <div className="h-full flex flex-col gap-4 min-h-0">
      {/* Tab bar + toolbar (compact single row) */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap shrink-0">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'scorecard' && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
              <Select value={periodType} onValueChange={(v) => handlePeriodTypeChange(v as KpiPeriodType)}>
                <SelectTrigger className="w-32 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {completionStats?.by_owner && completionStats.by_owner.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Users className="h-4 w-4 mr-1" />
                      {selectedOwnerIds.length === 0
                        ? 'All Owners'
                        : selectedOwnerIds.length === 1
                          ? completionStats.by_owner.find((o) => o.owner_id === selectedOwnerIds[0])?.owner_name ?? '1 owner'
                          : `${selectedOwnerIds.length} owners`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48 max-h-64 overflow-y-auto">
                    {completionStats.by_owner.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.owner_id}
                        checked={selectedOwnerIds.includes(o.owner_id)}
                        onCheckedChange={() =>
                          setSelectedOwnerIds((prev) =>
                            prev.includes(o.owner_id)
                              ? prev.filter((id) => id !== o.owner_id)
                              : [...prev, o.owner_id]
                          )
                        }
                      >
                        {o.owner_name}
                      </DropdownMenuCheckboxItem>
                    ))}
                    {selectedOwnerIds.length > 0 && (
                      <DropdownMenuItem
                        className="text-xs text-muted-foreground mt-1 border-t"
                        onClick={() => setSelectedOwnerIds([])}
                      >
                        Clear
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Compact completion stat */}
              {completionLabel && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1 rounded-md border bg-muted/30">
                        <CheckCircle className={`h-3.5 w-3.5 ${completionLabel.percentage === 100 ? 'text-green-500' : 'text-muted-foreground'}`} />
                        <span className="font-medium">{completionLabel.completed}/{completionLabel.total}</span>
                        <span className="hidden sm:inline">filled</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {completionLabel.percentage}% of KPIs have values for the current period
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  <FilterX className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCsv}
                disabled={!scorecard || totalKpiCount === 0}
              >
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isFetching}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              <ColumnVisibilityToggle />

              {canManage && (
                <Button size="sm" onClick={handleCreateKpi}>
                  <Plus className="h-4 w-4 mr-1" />
                  New KPI
                </Button>
              )}
          </div>
        )}
      </div>

      {activeTab === 'scorecard' && (
        <>
          {/* Main content area with optional side panel */}
          <div className="flex flex-1 min-h-0">
            {/* Left: scorecard content */}
            <div className="flex-1 min-w-0">
              {isLoading ? (
                <KpiScorecardSkeleton periodCount={4} />
              ) : !scorecard || totalKpiCount === 0 ? (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center">
                      <BarChart3 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No KPIs Found</h3>
                      <p className="text-muted-foreground mb-4">
                        {selectedOwnerIds.length > 0
                          ? 'No KPIs found for the selected owner(s).'
                          : selectedTeamId
                            ? 'No KPIs found for the selected team.'
                            : 'Get started by creating your first KPI.'}
                      </p>
                      {canManage && (
                        <Button onClick={handleCreateKpi}>
                          <Plus className="h-4 w-4 mr-2" />
                          Create KPI
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <KpiScorecard
                  scorecard={{
                    ...scorecard,
                    categories: orderedCategories,
                  }}
                  teamMap={teamMap}
                  onSaveValue={handleSaveValue}
                  onClearValue={handleClearValue}
                  savingCells={savingCells}
                  onReorder={reorder}
                  onEditKpi={handleEditKpi}
                  highlightedKpiId={highlightedKpiId}
                  searchText={searchText}
                  onSearchTextChange={setSearchText}
                  selectedCategory={selectedCategory}
                  onSelectedCategoryChange={setSelectedCategory}
                  selectedTeamId={selectedTeamId}
                  onSelectedTeamIdChange={(v) => {
                    setSelectedTeamId(v);
                    setSelectedOwnerIds([]);
                    persistTeamSelection(v);
                  }}
                  teams={teams}
                  selectedStatus={selectedStatus}
                  onSelectedStatusChange={setSelectedStatus}
                />
              )}
            </div>

            <SlidePanel open={!!(panelOpen && panelMode)} storageKey={PANEL_STORAGE_KEY}>
              {/* Panel header — sticky */}
              <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                <span className="text-sm font-semibold truncate">
                  {panelMode === 'create' ? 'New KPI' : 'Edit KPI'}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClosePanel}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {/* Panel body — scrollable */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
                {panelMode === 'create' ? (
                  <KpiForm
                    key="create-new"
                    open={panelOpen}
                    onClose={handleClosePanel}
                  />
                ) : editingKpiDefinition ? (
                  <KpiForm
                    key={editingKpiId}
                    open={panelOpen}
                    onClose={handleClosePanel}
                    kpi={editingKpiDefinition}
                    readOnly={!canManage}
                  />
                ) : null}
              </div>
            </SlidePanel>
          </div>
        </>
      )}

      {activeTab === 'guide' && <KpiGuide />}
    </div>
  );
}
